import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Badge as BadgeIcon,
  BookmarkStar,
  Crown,
  HandCoins,
} from "@/components/icons";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Typography from "@/components/ui/typography";
import {
  FoundersKeys,
  getFounderEarnings,
  markAgentsLibraryAnnouncementSeen,
} from "@/features/founders/api/founders";
import {
  getLicenseeProgression,
  LevelsKeys,
} from "@/features/founders/api/levels";
import { AgentsLibraryModal } from "@/features/founders/components/AgentsLibraryModal";
import EarningsOverview from "@/features/founders/components/EarningsOverview";
import IncomeChart from "@/features/founders/components/IncomeChart";
import LevelsDialog from "@/features/founders/components/LevelsDialog";
import StackLevelsWidget from "@/features/founders/components/StackLevelsWidget";
import { getInvoices, InvoicesKeys } from "@/features/invoices/api/invoices";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useStripeStatus } from "@/hooks/useStripeStatus";
import { FounderLevel } from "@/types/levels";
import { calculateMilestones, getNextLevel } from "@/utils/levelCalculations";

const FounderDashboard = () => {
  const { user } = useAuth0();
  const { user: currentUser, isLoading: userLoading } = useCurrentUser();
  const { stripeActive, stripeConnectUrl } = useStripeStatus();

  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: [FoundersKeys.Earnings],
    queryFn: getFounderEarnings,
  });

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: [InvoicesKeys.Invoices],
    queryFn: getInvoices,
  });

  const { data: progressionData } = useQuery({
    queryKey: [LevelsKeys.GET_LICENSEE_PROGRESSION, currentUser?._id],
    queryFn: () => getLicenseeProgression(currentUser!._id),
    enabled: !!currentUser,
  });

  const backendLevel = progressionData?.level; // "starter" | "certified" | "pro" | "x"
  const currentLevel: FounderLevel | null = (() => {
    if (!backendLevel) return null;
    if (backendLevel === "certified") return FounderLevel.STACK_CERTIFIED;
    if (backendLevel === "pro") return FounderLevel.STACK_PRO;
    if (backendLevel === "x") return FounderLevel.STACK_X;
    return null; // starter means no level unlocked
  })();

  const milestones =
    currentUser && earnings && invoices
      ? calculateMilestones(currentUser, earnings, invoices)
      : [];

  // Merge backend progression flags into milestones for widget rendering
  const milestonesWithBackend = progressionData
    ? milestones.map((m) => {
        if (m.id === "demo-approved") {
          return { ...m, completed: !!progressionData.demoPublished };
        }
        if (m.id === "playbook-completed") {
          return { ...m, completed: !!progressionData.courseCompleted };
        }
        if (m.id === "define-niche") {
          return {
            ...m,
            completed: m.completed || !!progressionData.nicheDefined,
          };
        }
        return m;
      })
    : milestones;

  const levelProgress = {
    currentLevel,
    nextLevel: getNextLevel(currentLevel),
    milestones: milestonesWithBackend,
    levelUnlocked: currentLevel !== null,
  };

  const isLevelsLoading = userLoading || earningsLoading || invoicesLoading;

  const [levelsOpen, setLevelsOpen] = useState(false);

  // Agents Library Announcement Modal
  const queryClient = useQueryClient();
  const [agentsModalOpen, setAgentsModalOpen] = useState(false);

  // Show modal only if user hasn't seen it and is a non-legacy founder
  useEffect(() => {
    if (
      currentUser &&
      !currentUser.hasSeenAgentsLibraryAnnouncement &&
      currentUser.nonAIFounder !== true
    ) {
      setAgentsModalOpen(true);
    }
  }, [currentUser]);

  const markSeenMutation = useMutation({
    mutationFn: markAgentsLibraryAnnouncementSeen,
    onSuccess: () => {
      // Update the cached user data to prevent modal from showing again
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const handleAgentsModalClose = (open: boolean) => {
    if (!open) {
      // Mark as seen when modal is dismissed (either by CTA or X button)
      markSeenMutation.mutate();
    }
    setAgentsModalOpen(open);
  };

  const allLevels = [
    FounderLevel.STACK_CERTIFIED,
    FounderLevel.STACK_PRO,
    FounderLevel.STACK_X,
  ];
  const startIndex = levelProgress.nextLevel
    ? allLevels.indexOf(levelProgress.nextLevel)
    : -1;
  const visibleLevels = startIndex >= 0 ? allLevels.slice(startIndex) : [];

  return (
    <DashboardLayout>
      <div>
        {/* Header */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Typography size="h1" className="text-[#1e1e24]">
              {user?.given_name
                ? `${user.given_name}'s Dashboard`
                : "Dashboard"}
            </Typography>
            {levelProgress.currentLevel && (
              <span
                className={
                  `h-6 inline-flex items-center rounded px-[6px] py-[4px] text-xs font-semibold whitespace-nowrap ` +
                  (levelProgress.currentLevel === FounderLevel.STACK_CERTIFIED
                    ? "bg-[#dcddfa] text-[#4f55e8]"
                    : levelProgress.currentLevel === FounderLevel.STACK_PRO
                      ? "bg-[#def5f9] text-[#3eacbc]"
                      : "bg-[#e6f0cc] text-[#83b300]")
                }
              >
                {levelProgress.currentLevel}
                {levelProgress.currentLevel ===
                  FounderLevel.STACK_CERTIFIED && (
                  <BadgeIcon className="ml-1 h-4 w-4" />
                )}
                {levelProgress.currentLevel === FounderLevel.STACK_PRO && (
                  <BookmarkStar className="ml-1 h-4 w-4" />
                )}
                {levelProgress.currentLevel === FounderLevel.STACK_X && (
                  <Crown className="ml-1 h-4 w-4" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* Subtle Stripe note - only when not connected */}
        {!stripeActive && stripeConnectUrl && (
          <div className="mt-4 bg-[#F9F9F9] rounded-2xl px-4 py-3 flex items-center gap-3">
            <HandCoins className="h-5 w-5 text-[#4F55E8] flex-shrink-0" />
            <p className="text-sm text-[#767677]">
              Connect your Stripe account to receive payouts.{" "}
              <a
                href={stripeConnectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4f55e8] hover:underline"
              >
                Connect
              </a>
            </p>
          </div>
        )}

        {/* To do / Stack levels - only for non-legacy founders */}
        {currentUser?.nonAIFounder !== true && (
          <div className="mt-8 mb-8 md:mt-16 md:mb-16">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="space-y-1">
                <Typography size="h2" className="text-[#1e1e24]">
                  To do
                </Typography>
                <Typography size="b2" className="text-[#767677]">
                  Complete each task to unlock the benefits at each Stack Level.
                </Typography>
              </div>
              <button
                onClick={() => setLevelsOpen(true)}
                className="hidden sm:block h-7 rounded-md px-3 text-[10px] font-semibold border border-[#e8e8e8] text-[#1e1e24] self-start sm:self-auto"
              >
                See levels
              </button>
            </div>

            <div
              className={`grid gap-4 ${
                isLevelsLoading
                  ? "grid-cols-1 md:grid-cols-3"
                  : levelProgress.currentLevel
                    ? "grid-cols-1 md:grid-cols-2"
                    : visibleLevels.length === 2
                      ? "grid-cols-1 md:grid-cols-2"
                      : "grid-cols-1 md:grid-cols-3"
              } mt-0`}
            >
              {isLevelsLoading ? (
                <>
                  <StackLevelsWidget
                    levelProgress={levelProgress}
                    isLoading
                    hideStatusBenefitsLink
                    initialLevel={FounderLevel.STACK_CERTIFIED}
                  />
                  <div className="hidden md:block">
                    <StackLevelsWidget
                      levelProgress={levelProgress}
                      isLoading
                      hideStatusBenefitsLink
                      initialLevel={FounderLevel.STACK_PRO}
                    />
                  </div>
                  <div className="hidden md:block">
                    <StackLevelsWidget
                      levelProgress={levelProgress}
                      isLoading
                      hideStatusBenefitsLink
                      initialLevel={FounderLevel.STACK_X}
                    />
                  </div>
                </>
              ) : (
                visibleLevels.map((lvl, index) => (
                  <div key={lvl} className={index > 0 ? "hidden md:block" : undefined}>
                    <StackLevelsWidget
                      levelProgress={levelProgress}
                      isLoading={isLevelsLoading}
                      hideStatusBenefitsLink
                      initialLevel={lvl}
                    />
                  </div>
                ))
              )}
            </div>

            <button
              onClick={() => setLevelsOpen(true)}
              className="sm:hidden mt-4 h-7 rounded-md px-3 text-[10px] font-semibold border border-[#e8e8e8] text-[#1e1e24]"
            >
              See levels
            </button>
          </div>
        )}

        {/* Earnings overview section */}
        <div className="space-y-4">
          <Typography size="h2" className="text-[#1e1e24]">
            Earnings overview
          </Typography>
          {/* Metric cards to be updated next */}
          <EarningsOverview earnings={earnings} isLoading={earningsLoading} />
        </div>

        {/* Monthly earnings graph */}
        <IncomeChart earnings={earnings} isLoading={earningsLoading} />
      </div>
      <LevelsDialog
        open={levelsOpen}
        onOpenChange={setLevelsOpen}
        currentLevel={levelProgress.currentLevel}
      />
      <AgentsLibraryModal
        open={agentsModalOpen}
        onOpenChange={handleAgentsModalClose}
      />
    </DashboardLayout>
  );
};

export default FounderDashboard;
